import type { JSX } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { generateSlug } from '~/utils/slug';

import { Input } from '~/components/Field';

import type { OnboardingFormData } from './schemas';

interface OrgDetailsStepProps {
  form: UseFormReturn<OnboardingFormData>;
}

export function OnboardingDetails({ form }: OrgDetailsStepProps): JSX.Element {
  const {
    formState: { errors },
    register,
    setValue,
    watch,
  } = form;

  // Extract ref/onBlur from register (onChange is filtered by HeroUI, so we use onValueChange)
  const { onBlur: nameOnBlur, ref: nameRef } = register('name');
  const { onBlur: slugOnBlur, ref: slugRef } = register('slug');

  const name = watch('name');
  const slug = watch('slug');

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Organization Details</h2>
        <p className="text-sm">
          Let's start by naming your organization. This information helps identify your workspace.
        </p>
      </div>

      <Input
        id="name"
        name="name"
        ref={nameRef}
        labelPlacement="outside-top"
        label="Organization Name"
        placeholder="Acme Inc"
        description="The display name for your organization (3-255 characters)."
        isRequired
        isInvalid={!!errors.name}
        errorMessage={errors.name?.message}
        value={name}
        onValueChange={value => {
          void setValue('name', value, { shouldDirty: true });
        }}
        onBlur={e => {
          void nameOnBlur(e);
          // Auto-generate slug on blur if name valid and slug empty
          const trimmedName = name?.trim() || '';
          if (trimmedName.length >= 3 && !slug) {
            setValue('slug', generateSlug(trimmedName), { shouldValidate: true });
          }
        }}
      />

      <Input
        id="slug"
        name="slug"
        ref={slugRef}
        labelPlacement="outside-top"
        label="Organization Slug"
        placeholder="acme-inc"
        description="URL-friendly identifier. Only lowercase letters, numbers, and hyphens (3-100 characters)."
        isRequired
        isInvalid={!!errors.slug}
        errorMessage={errors.slug?.message}
        value={slug}
        onValueChange={value => {
          void setValue('slug', value, { shouldDirty: true });
        }}
        onBlur={e => {
          void slugOnBlur(e);
        }}
      />
    </div>
  );
}
